import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  BuyOrSelltrend,
  LastEventType,
  SignalType,
  Sma,
  TradeEventCause,
  useOptimizeRsiSignalMutation,
  useSignalTradeHistoryLazyQuery,
  useStockDetailLazyQuery,
  useTestSignalsForStockMutation,
  useUpdateRsiSignalMutation,
  useUpdateSmaSignalMutation,
} from '../generated/graphql';
import { formatSmaLabel } from '../formatters/stock.formatters';

type OptimizeResult = {
  buySignal: number;
  sellSignal: number;
  stopLoss: number;
  gain: number;
};

type Props = {
  stockId: string;
  stockName: string;
  stockSymbol: string;
  currentPrice: string;
  trend: string;
  onClose: () => void;
};

type ActiveTab = 'RSI' | 'SMA';

const SMA_OPTIONS: Sma[] = [Sma.Sma_20, Sma.Sma_50, Sma.Sma_100];

const StockSignalDrawer: React.FC<Props> = ({
  stockId,
  stockName,
  stockSymbol,
  currentPrice,
  trend,
  onClose,
}) => {
  const [fetchDetail, { data, loading }] = useStockDetailLazyQuery({
    variables: { id: stockId },
  });

  const [updateRsiSignal, { loading: savingRsi }] = useUpdateRsiSignalMutation();
  const [testSignalsForStock, { loading: testing }] =
    useTestSignalsForStockMutation();
  const [optimizeRsiSignal, { loading: optimizing }] =
    useOptimizeRsiSignalMutation();
  const [updateSmaSignal, { loading: savingSma }] = useUpdateSmaSignalMutation();

  const [activeTab, setActiveTab] = useState<ActiveTab>('RSI');

  // Trades history state
  const [tradesOpen, setTradesOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [fetchTradeHistory, { loading: loadingTrades, data: tradesData }] =
    useSignalTradeHistoryLazyQuery();

  // RSI state
  const [buySignal, setBuySignal] = useState('');
  const [sellSignal, setSellSignal] = useState('');
  const [maxBuys, setMaxBuys] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [optimizeResult, setOptimizeResult] = useState<OptimizeResult | null>(
    null,
  );

  // SMA state
  const [smaUsedSma, setSmaUsedSma] = useState<Sma>(Sma.Sma_20);
  const [smaDaysInBuyTrend, setSmaDaysInBuyTrend] = useState('');
  const [smaDaysInSellTrend, setSmaDaysInSellTrend] = useState('');
  const [smaMaxBuys, setSmaMaxBuys] = useState('');
  const [smaStopLoss, setSmaStopLoss] = useState('');

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    const rsi = data?.stock?.currentUser?.signals?.rsi;
    if (rsi) {
      setBuySignal(String(rsi.buySignal));
      setSellSignal(String(rsi.sellSignal));
      setMaxBuys(String(rsi.maxBuys));
      setStopLoss(String(rsi.stopLoss));
    }
    const sma = data?.stock?.currentUser?.signals?.sma;
    if (sma) {
      setSmaUsedSma(sma.usedSma);
      setSmaDaysInBuyTrend(String(sma.daysInBuyTrend));
      setSmaDaysInSellTrend(String(sma.daysInSellTrend));
      setSmaMaxBuys(String(sma.maxBuys));
      setSmaStopLoss(String(sma.stopLoss));
    }
  }, [data]);

  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab);
    setTradesOpen(false);
    setShowAll(false);
  };

  const handleTestLive = async () => {
    await testSignalsForStock({ variables: { stockId } });
    fetchDetail();
  };

  const handleOptimize = async () => {
    setOptimizeResult(null);
    const result = await optimizeRsiSignal({ variables: { stockId } });
    if (result.data?.optimizeRsiSignal) {
      setOptimizeResult(result.data.optimizeRsiSignal);
    }
  };

  const handleApplyOptimized = () => {
    if (!optimizeResult) return;
    setBuySignal(String(optimizeResult.buySignal));
    setSellSignal(String(optimizeResult.sellSignal));
    setStopLoss(String(optimizeResult.stopLoss));
    setOptimizeResult(null);
  };

  const handleSaveRsi = async () => {
    await updateRsiSignal({
      variables: {
        stockId,
        input: {
          buySignal: parseInt(buySignal, 10),
          sellSignal: parseInt(sellSignal, 10),
          maxBuys: parseInt(maxBuys, 10),
          stopLoss: parseInt(stopLoss, 10),
        },
      },
    });
    onClose();
  };

  const handleSaveSma = async () => {
    const existingSma = data?.stock?.currentUser?.signals?.sma;
    await updateSmaSignal({
      variables: {
        stockId,
        input: {
          usedSma: smaUsedSma,
          buyTrend: existingSma?.buyTrend ?? BuyOrSelltrend.Asc,
          daysInBuyTrend: parseInt(smaDaysInBuyTrend, 10),
          sellTrend: existingSma?.sellTrend ?? BuyOrSelltrend.Desc,
          daysInSellTrend: parseInt(smaDaysInSellTrend, 10),
          maxBuys: parseInt(smaMaxBuys, 10),
          stopLoss: parseInt(smaStopLoss, 10),
        },
      },
    });
    onClose();
  };

  const lastRsiEvent = data?.stock?.currentUser?.signals?.rsi?.lastEvent;
  const trendColor = trend.startsWith('+')
    ? 'text-green-600'
    : trend.startsWith('-')
      ? 'text-red-600'
      : 'text-gray-400';

  const saving = savingRsi || savingSma;

  /**
   * Section dépliable « Historique des trades » — partagée par RSI et SMA.
   * Déclenche la query au premier clic (lazy), puis bascule l'état ouvert/fermé.
   */
  const renderTradesHistory = (signalType: ActiveTab) => {
    const testId = `trades-history-toggle-${signalType}`;
    const apolloSignalType =
      signalType === 'RSI' ? SignalType.Rsi : SignalType.Sma;
    const trades = tradesData?.signalTradeHistory ?? [];
    const visibleTrades = showAll ? trades : trades.slice(-5);
    const avgGain =
      trades.length > 0
        ? trades.reduce((acc, t) => acc + t.gainPercent, 0) / trades.length
        : 0;

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          data-testid={testId}
          onClick={async () => {
            if (!tradesOpen) {
              await fetchTradeHistory({
                variables: { stockId, signalType: apolloSignalType },
              });
            }
            setTradesOpen(!tradesOpen);
            setShowAll(false);
          }}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <span>
            Historique des trades
            {tradesData ? ` (${tradesData.signalTradeHistory.length})` : ''}
          </span>
          <span>{tradesOpen ? '▲' : '▼'}</span>
        </button>

        {tradesOpen && (
          <div className="p-3">
            {loadingTrades ? (
              <p className="text-sm text-gray-500 text-center">Chargement…</p>
            ) : trades.length === 0 ? (
              <p className="text-sm text-gray-500 text-center">
                Aucun trade sur l&apos;historique
              </p>
            ) : (
              <>
                <ul className="space-y-1 mb-3">
                  {visibleTrades.map((trade, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between text-xs text-gray-600"
                    >
                      <span>
                        {new Date(trade.sellDate).toLocaleDateString('fr-FR')}
                      </span>
                      <span
                        className={
                          trade.cause === TradeEventCause.Stop
                            ? 'text-orange-600'
                            : 'text-gray-600'
                        }
                      >
                        {trade.cause === TradeEventCause.Stop
                          ? 'STOP'
                          : 'VENTE'}
                      </span>
                      <span
                        className={
                          trade.gainPercent >= 0
                            ? 'text-green-600 font-medium'
                            : 'text-red-600 font-medium'
                        }
                      >
                        {trade.gainPercent >= 0 ? '+' : ''}
                        {new Intl.NumberFormat('fr-FR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(trade.gainPercent * 100)}{' '}
                        %
                      </span>
                    </li>
                  ))}
                </ul>

                {!showAll && trades.length > 5 && (
                  <button
                    onClick={() => setShowAll(true)}
                    className="text-xs text-indigo-600 hover:underline mb-2"
                  >
                    Voir tout ({trades.length} trades)
                  </button>
                )}

                <div className="pt-2 border-t border-gray-200 text-xs text-gray-600 flex justify-between">
                  <span>Gain moyen</span>
                  <span
                    className={
                      avgGain >= 0
                        ? 'text-green-700 font-semibold'
                        : 'text-red-700 font-semibold'
                    }
                  >
                    {avgGain >= 0 ? '+' : ''}
                    {new Intl.NumberFormat('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(avgGain * 100)}{' '}
                    % / {trades.length} trade
                    {trades.length > 1 ? 's' : ''}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const drawer = (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-xl z-50 flex flex-col animate-slide-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold">{stockName}</h2>
            <p className="text-xs text-gray-500">
              {stockSymbol} · {currentPrice}
              {trend !== '-' && (
                <span className={`ml-1 ${trendColor}`}>{trend}</span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="flex border-b border-gray-200 px-6">
          <button
            onClick={() => handleTabChange('RSI')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'RSI'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            RSI
          </button>
          <button
            onClick={() => handleTabChange('SMA')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'SMA'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            SMA
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {loading ? (
            <div className="text-center text-gray-500">Chargement...</div>
          ) : activeTab === 'RSI' ? (
            <>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Configuration RSI
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="rsi-buy-signal"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Seuil achat
                    </label>
                    <input
                      id="rsi-buy-signal"
                      type="number"
                      value={buySignal}
                      onChange={(e) => setBuySignal(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="rsi-sell-signal"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Seuil vente
                    </label>
                    <input
                      id="rsi-sell-signal"
                      type="number"
                      value={sellSignal}
                      onChange={(e) => setSellSignal(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="rsi-max-buys"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Max achats
                    </label>
                    <input
                      id="rsi-max-buys"
                      type="number"
                      value={maxBuys}
                      onChange={(e) => setMaxBuys(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="rsi-stop-loss"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Stop-loss{' '}
                      <span className="text-gray-400 font-normal">%</span>
                    </label>
                    <input
                      id="rsi-stop-loss"
                      type="number"
                      value={stopLoss}
                      onChange={(e) => setStopLoss(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={handleOptimize}
                    disabled={optimizing || loading}
                    className="w-full px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 rounded-md hover:bg-orange-100 focus:outline-hidden focus:ring-2 focus:ring-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {optimizing
                      ? '⟳ Optimisation en cours…'
                      : '✨ Optimiser les paramètres'}
                  </button>

                  {optimizeResult && (
                    <div className="mt-3 bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <p className="text-sm font-semibold text-orange-800 mb-2">
                        ✨ Meilleurs paramètres trouvés
                      </p>
                      <p className="text-sm text-orange-700 mb-1">
                        Gain historique :{' '}
                        <span className="font-semibold">
                          {optimizeResult.gain >= 0 ? '+' : ''}
                          {new Intl.NumberFormat('fr-FR', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }).format(optimizeResult.gain)}{' '}
                          %
                        </span>
                      </p>
                      <p className="text-sm text-orange-700 mb-3">
                        Achat : {optimizeResult.buySignal} · Vente :{' '}
                        {optimizeResult.sellSignal} · SL :{' '}
                        {optimizeResult.stopLoss} %
                      </p>
                      <button
                        onClick={handleApplyOptimized}
                        className="w-full px-3 py-1.5 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 focus:outline-hidden focus:ring-2 focus:ring-orange-500 transition-colors"
                      >
                        Appliquer ces paramètres
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {lastRsiEvent && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    Dernier événement
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm">
                    <span
                      className={`font-semibold ${lastRsiEvent.type === LastEventType.Buy ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {lastRsiEvent.type === LastEventType.Buy ? 'ACHAT' : 'VENTE'}
                    </span>
                    {' · '}
                    {new Date(lastRsiEvent.date).toLocaleDateString('fr-FR')}
                    {' · '}
                    {new Intl.NumberFormat('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(
                      lastRsiEvent.type === LastEventType.Buy
                        ? lastRsiEvent.buyValue
                        : (lastRsiEvent.sellValue ?? lastRsiEvent.buyValue),
                    )}{' '}
                    €
                  </div>
                </div>
              )}

              {renderTradesHistory('RSI')}
            </>
          ) : (
            <>
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Configuration SMA
                </h3>

                <div className="mb-4">
                  <p className="block text-sm font-medium text-gray-700 mb-2">
                    Période SMA
                  </p>
                  <div className="flex gap-4">
                    {SMA_OPTIONS.map((opt) => (
                      <label key={opt} className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          name="sma-period"
                          value={opt}
                          checked={smaUsedSma === opt}
                          onChange={() => setSmaUsedSma(opt)}
                          className="accent-blue-600"
                        />
                        <span className="text-sm text-gray-700">
                          {formatSmaLabel(opt)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="sma-days-buy"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Jours tendance achat
                    </label>
                    <input
                      id="sma-days-buy"
                      type="number"
                      value={smaDaysInBuyTrend}
                      onChange={(e) => setSmaDaysInBuyTrend(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="sma-days-sell"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Jours tendance vente
                    </label>
                    <input
                      id="sma-days-sell"
                      type="number"
                      value={smaDaysInSellTrend}
                      onChange={(e) => setSmaDaysInSellTrend(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="sma-max-buys"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Max achats
                    </label>
                    <input
                      id="sma-max-buys"
                      type="number"
                      value={smaMaxBuys}
                      onChange={(e) => setSmaMaxBuys(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="sma-stop-loss"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Stop-loss{' '}
                      <span className="text-gray-400 font-normal">%</span>
                    </label>
                    <input
                      id="sma-stop-loss"
                      type="number"
                      value={smaStopLoss}
                      onChange={(e) => setSmaStopLoss(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>

              {renderTradesHistory('SMA')}
            </>
          )}
        </div>

        {activeTab === 'RSI' && (
          <div className="px-6 py-3 border-t border-gray-200">
            <button
              onClick={handleTestLive}
              disabled={testing || loading}
              className="w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {testing ? 'Test en cours...' : '▶ Tester en live'}
            </button>
          </div>
        )}

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-gray-500 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={activeTab === 'RSI' ? handleSaveRsi : handleSaveSma}
            disabled={saving || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(drawer, document.body);
};

export default StockSignalDrawer;
